// src/components/planner/modals/ManualSessionModal.tsx

import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';

const API_BASE_URL = '/api';

const OTHER_COLORS = [
    '#f59e0b', '#84cc16', '#06b6d4', '#8b5cf6',
    '#f43f5e', '#14b8a6', '#fb923c', '#a78bfa'
];

interface ManualSessionModalProps {
    onClose: () => void;
    onSave: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const ManualSessionModal: React.FC<ManualSessionModalProps> = ({ onClose, onSave, onShowToast }) => {
    const [formData, setFormData] = useState({
        type: 'flashcards',
        subject: '',
        startTime: new Date().toISOString().slice(0, 16),
        duration: '',
        notes: '',
        customType: '',
        color: OTHER_COLORS[0]
    });

    const [savedCustomTypes, setSavedCustomTypes] = useState<{ name: string, color: string }[]>([]);
    const [selectedChip, setSelectedChip] = useState('');

    useEffect(() => {
        const load = async () => {
            try {
                const token = localStorage.getItem('accessToken');
                const res = await fetch(`${API_BASE_URL}/planner/custom-study-types`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setSavedCustomTypes(data.types || []);
                }
            } catch (e) { }
        };
        load();
    }, []);

    const handleChipSelect = (ct: { name: string, color: string }) => {
        setSelectedChip(ct.name);
        setFormData(prev => ({ ...prev, customType: ct.name, color: ct.color }));
    };

    const handleCustomTypeInput = (value: string) => {
        setFormData(prev => ({ ...prev, customType: value }));
        setSelectedChip('');
    };

    const handleTypeChange = (newType: string) => {
        setFormData(prev => ({ ...prev, type: newType, customType: '', color: OTHER_COLORS[0] }));
        setSelectedChip('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.duration) {
            onShowToast('Informe a duração', 'error');
            return;
        }

        // Se é tipo "outro" novo (não chip), persiste no backend
        if (formData.type === 'other' && formData.customType.trim() && !selectedChip) {
            try {
                const token = localStorage.getItem('accessToken');
                await fetch(`${API_BASE_URL}/planner/custom-study-types`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ name: formData.customType.trim(), color: formData.color })
                });
            } catch (e) { }
        }

        try {
            const token = localStorage.getItem('accessToken');
            const payload = {
                type: formData.type,
                subject: formData.subject || undefined,
                startTime: formData.startTime,
                duration: parseInt(formData.duration),
                notes: formData.notes || undefined,
                customType: formData.type === 'other' ? (formData.customType.trim() || undefined) : undefined,
                color: formData.type === 'other' ? formData.color : undefined
            };

            const response = await fetch(`${API_BASE_URL}/planner/study-sessions/manual`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                onShowToast('Sessão registrada!', 'success');
                onSave();
            } else {
                onShowToast('Erro ao registrar sessão', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao registrar sessão', 'error');
        }
    };

    return (
        <div className="plnr-modal-overlay" onClick={onClose}>
            <div className="plnr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="plnr-modal-header">
                    <h3>Registrar Estudo Passado</h3>
                    <button className="plnr-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="plnr-modal-body">

                        {/* TIPO */}
                        <div className="plnr-form-group">
                            <label>Tipo de estudo</label>
                            <select
                                value={formData.type}
                                onChange={(e) => handleTypeChange(e.target.value)}
                                className="plnr-select"
                            >
                                <option value="flashcards">Flashcards</option>
                                <option value="questions">Questões</option>
                                <option value="reading">Leitura</option>
                                <option value="review">Revisão</option>
                                <option value="mindmap">Mapa Mental</option>
                                <option value="notes">Resumos e Anotações</option>
                                <option value="class">Aula</option>
                                <option value="other">Outro</option>
                            </select>
                        </div>

                        {/* CAMPOS DE "OUTRO" */}
                        {formData.type === 'other' && (
                            <>
                                {savedCustomTypes.length > 0 && (
                                    <div className="plnr-form-group">
                                        <label>Tipos anteriores</label>
                                        <div className="plnr-custom-type-chips">
                                            {savedCustomTypes.map(ct => (
                                                <button
                                                    key={ct.name}
                                                    type="button"
                                                    className={`plnr-custom-type-chip ${selectedChip === ct.name ? 'active' : ''}`}
                                                    style={{
                                                        borderColor: ct.color,
                                                        backgroundColor: selectedChip === ct.name ? ct.color : 'transparent',
                                                        color: selectedChip === ct.name ? 'white' : ct.color
                                                    }}
                                                    onClick={() => handleChipSelect(ct)}
                                                >
                                                    {ct.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="plnr-form-group">
                                    <label>{savedCustomTypes.length > 0 ? 'Ou digite um novo tipo' : 'Nome do tipo de estudo'}</label>
                                    <input
                                        type="text"
                                        value={formData.customType}
                                        onChange={(e) => handleCustomTypeInput(e.target.value)}
                                        placeholder="Ex: Mapas Conceituais"
                                        className="plnr-input"
                                    />
                                </div>

                                {/* CORES — só para tipo novo */}
                                {!selectedChip && (
                                    <div className="plnr-form-group">
                                        <label>Cor</label>
                                        <div className="plnr-color-grid">
                                            {OTHER_COLORS.map(color => (
                                                <button
                                                    key={color}
                                                    type="button"
                                                    className={`plnr-color-option ${formData.color === color ? 'active' : ''}`}
                                                    style={{ backgroundColor: color }}
                                                    onClick={() => setFormData(prev => ({ ...prev, color }))}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* ASSUNTO */}
                        <div className="plnr-form-group">
                            <label>Matéria ou assunto (opcional)</label>
                            <input
                                type="text"
                                value={formData.subject}
                                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                                placeholder="Ex: Cardiologia"
                                className="plnr-input"
                            />
                        </div>

                        {/* DATA/HORA */}
                        <div className="plnr-form-group">
                            <label>Quando estudou? *</label>
                            <input
                                type="datetime-local"
                                value={formData.startTime}
                                onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                                className="plnr-input"
                                required
                            />
                        </div>

                        {/* DURAÇÃO */}
                        <div className="plnr-form-group">
                            <label>Duração (minutos) *</label>
                            <input
                                type="number"
                                value={formData.duration}
                                onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                                placeholder="Ex: 60"
                                className="plnr-input"
                                required
                                min="1"
                            />
                        </div>

                        {/* NOTAS */}
                        <div className="plnr-form-group">
                            <label>Notas (opcional)</label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                placeholder="O que estudou, como foi..."
                                className="plnr-textarea"
                                rows={3}
                            />
                        </div>
                    </div>

                    <div className="plnr-modal-footer">
                        <button type="button" className="plnr-btn secondary" onClick={onClose}>
                            Cancelar
                        </button>
                        <button type="submit" className="plnr-btn primary">
                            <Save size={16} />
                            Registrar
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ManualSessionModal;